import { theme, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { useKanagawaChartColors, useKanagawaPalette } from '@aid/theme';
import type { ServicioCount } from '@aid/shared';
import { EmptyState } from '../../../components/EmptyState';
import { DashboardCard } from '../../../components/DashboardCard';
import { useT } from '../../../locales';
import { DATE_LOCALE } from '../constants';
import './ServiciosByEstadoChart.css';

const { Text } = Typography;

interface ServiciosByEstadoChartProps {
  servicios: ServicioCount[];
}

function formatPercentage(count: number, total: number): string {
  if (total <= 0) return '0%';
  const percentage = (count / total) * 100;
  const formatted = new Intl.NumberFormat(DATE_LOCALE, { maximumFractionDigits: 1 }).format(
    percentage,
  );
  return `${formatted}%`;
}

/** Compact tabular composition of the current service portfolio by status. */
export function ServiciosByEstadoChart({ servicios }: ServiciosByEstadoChartProps) {
  const t = useT();
  const { token } = theme.useToken();
  const chartColors = useKanagawaChartColors();
  const palette = useKanagawaPalette();

  // Keep a stable color per status key even when the API changes row order.
  const colorByKey = new Map<string, string>();
  servicios.forEach((service, index) => {
    if (!colorByKey.has(service.estadoKey)) {
      const color = chartColors.length > 0 ? chartColors[index % chartColors.length] : undefined;
      colorByKey.set(service.estadoKey, color ?? palette.blue);
    }
  });

  const totalServicios = servicios.reduce((sum, service) => sum + service.count, 0);
  const rows = [...servicios].sort((a, b) => b.count - a.count);
  const tableStyle = {
    '--services-border': token.colorBorderSecondary,
    '--services-muted': token.colorTextSecondary,
    '--services-text': token.colorText,
  } as CSSProperties;

  return (
    <DashboardCard
      style={{ height: '100%' }}
      bodyStyle={{ padding: '16px 22px 20px' }}
      title={t('dashboard.serviciosTitle')}
      subtitle={t('dashboard.serviciosSubtitle')}
    >
      {servicios.length === 0 ? (
        <EmptyState
          compact
          title={t('dashboard.serviciosSinDatos')}
          description={t('dashboard.serviciosSinDatosDesc')}
        />
      ) : (
        <div className="services-status" style={tableStyle}>
          <div className="services-status__total" data-testid="servicios-total">
            <Text type="secondary">{t('dashboard.serviciosTotal')}</Text>
            <Text strong>{totalServicios.toLocaleString(DATE_LOCALE)}</Text>
          </div>
          <table className="services-status__table" aria-label={t('dashboard.serviciosTableAria')}>
            <thead>
              <tr>
                <th scope="col">{t('dashboard.serviciosEstado')}</th>
                <th scope="col">{t('dashboard.serviciosCantidad')}</th>
                <th scope="col">{t('dashboard.serviciosPorcentaje')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.estadoKey} data-testid={`servicios-row-${item.estadoKey}`}>
                  <td>
                    <div className="services-status__label">
                      <span
                        className="services-status__dot"
                        aria-hidden
                        style={{ backgroundColor: colorByKey.get(item.estadoKey) ?? palette.blue }}
                      />
                      <span title={item.estadoValue}>{item.estadoValue}</span>
                    </div>
                  </td>
                  <td>{item.count.toLocaleString(DATE_LOCALE)}</td>
                  <td>{formatPercentage(item.count, totalServicios)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardCard>
  );
}
